// Localised UI strings — FR (primary) + EN + ES + IT + DE + ZH + NL
// `native` is the endonym — each language named in itself, the one spelling
// every guest can read whatever the current UI language is (menu radio rows).
//
// ⚑ UNE LANGUE PAR import() (audit du 10/09/2026, PERFORMANCE n°4 ; livré
// dans la seconde vague). Les sept dictionnaires vivaient dans ce fichier,
// dans un seul objet : 356 919 octets minifiés, le PREMIER contributeur de
// main.js, et 108 036 octets gzippés de six langues qu'un visiteur ne lit pas,
// transportés sur chacune des 2 317 adresses du site.
//
// Désormais chaque langue est un fichier de `./i18n/` et son propre chunk :
//   · le FR reste dans le tronc — `t()` est appelé de façon synchrone partout,
//     y compris avant le montage (App.js `RouteFallback`, les frontières
//     d'erreur), et c'est vers lui que toute clef manquante retombe ;
//   · les six autres arrivent par `loadLang(code)`, et l'application ATTEND
//     ce chargement avant son premier rendu dans cette langue (index.js via
//     lib/bootLang.js ; SiteApp `useLoadedLang` au changement d'adresse ;
//     MedardExperience `setLang` au changement de langue). Un dictionnaire
//     absent ne montre jamais une clef nue : `t()` sert le français en
//     attendant — c'est un « flash » à éviter, pas une casse.
//
// `I18N` est le REGISTRE VIVANT : `{ fr }` au démarrage, puis chaque langue
// chargée. Les tests jest le remplissent d'emblée (setupTests.js →
// ./i18n/all.js) pour que `t('de', …)` y réponde en allemand comme dans le
// navigateur une fois le chunk arrivé ; un test dédié (i18nComplet.test.js)
// tient le comportement AVANT chargement sur un registre neuf.
import FR from './i18n/fr';

export const LANGUAGES = [
    { code: 'fr', label: 'FR', native: 'Français', name_fr: 'Français', name_en: 'French' },
    { code: 'en', label: 'EN', native: 'English', name_fr: 'Anglais', name_en: 'English' },
    { code: 'es', label: 'ES', native: 'Español', name_fr: 'Espagnol', name_en: 'Spanish' },
    { code: 'it', label: 'IT', native: 'Italiano', name_fr: 'Italien', name_en: 'Italian' },
    { code: 'de', label: 'DE', native: 'Deutsch', name_fr: 'Allemand', name_en: 'German' },
    { code: 'zh', label: '中', native: '中文', name_fr: 'Chinois', name_en: 'Chinese' },
    { code: 'nl', label: 'NL', native: 'Nederlands', name_fr: 'Néerlandais', name_en: 'Dutch' },
];

// One `import()` per language, each under its own chunk name so the shell can
// hint it (plugins/preload-lang.js) and a build can weigh it. The FR loader
// exists so that `loadLang` has one shape for every offered code.
const LOADERS = {
    fr: () => Promise.resolve({ default: FR }),
    en: () => import(/* webpackChunkName: "lang-en" */ './i18n/en'),
    es: () => import(/* webpackChunkName: "lang-es" */ './i18n/es'),
    it: () => import(/* webpackChunkName: "lang-it" */ './i18n/it'),
    de: () => import(/* webpackChunkName: "lang-de" */ './i18n/de'),
    zh: () => import(/* webpackChunkName: "lang-zh" */ './i18n/zh'),
    nl: () => import(/* webpackChunkName: "lang-nl" */ './i18n/nl'),
};

/** The live registry: FR always, every other language once loaded. */
export const I18N = { fr: FR };

const pending = {};

/** True when `code` is an offered language (whatever its loading state). */
export const isOfferedLang = (code) => Boolean(LOADERS[code]);

/** True when `t(code, …)` would answer in that language right now. */
export const isLangLoaded = (code) => Boolean(I18N[code]);

/**
 * Load one language's dictionary into the registry. Resolves with the
 * dictionary — at once for FR or a language already loaded, after its chunk
 * otherwise. An unknown code resolves with FR (what `t()` would serve). A
 * failed chunk rejects, and the caller decides (index.js renders anyway:
 * French is a worse first screen than none, never the reverse).
 */
export function loadLang(code) {
    if (I18N[code]) return Promise.resolve(I18N[code]);
    const loader = LOADERS[code];
    if (!loader) return Promise.resolve(FR);
    if (!pending[code]) {
        pending[code] = loader().then(
            (mod) => {
                I18N[code] = mod.default;
                delete pending[code];
                return I18N[code];
            },
            (err) => {
                delete pending[code];
                throw err;
            },
        );
    }
    return pending[code];
}

/** Load several languages at once (duplicates and FR cost nothing). */
export function loadLangs(codes) {
    return Promise.all([...new Set(codes)].map(loadLang));
}

/**
 * Register dictionaries synchronously — for the test suite (setupTests.js)
 * and for tooling that holds every language in hand. Never called by the app.
 */
export function registerDictionaries(dicts) {
    for (const [code, dict] of Object.entries(dicts)) {
        if (LOADERS[code] && dict) I18N[code] = dict;
    }
}

export const t = (lang, key) => {
    const parts = key.split('.');
    let v = I18N[lang] || I18N.fr;
    for (const p of parts) v = v?.[p];
    // Fallback to FR if missing
    if (v === undefined || v === null) {
        let fb = I18N.fr;
        for (const p of parts) fb = fb?.[p];
        return fb ?? key;
    }
    return v;
};
